import dayjs from 'dayjs';
import { pick } from 'lodash';
import * as yup from 'yup';

import {
  DaysOfWeek,
  FoodCategories,
  Gender,
  Offer,
  OfferAccessType,
  OfferCategory,
  OfferStatus,
  Profession,
  ShippingRegion,
  VenueCreateOfferInput,
  VenueUpdateOfferInput,
} from '../../../graphql/API';
import { datetimeWithTimezone, getTimeInTimezone } from '../../../utils/dateV2';

export const OFFER_TYPES = ['public', 'private'] as const;
export const OFFER_CATEGORIES = Object.values(OfferCategory);
export const OFFER_FOOD_CATEGORIES = Object.values(FoodCategories);
export const DAYS_OF_WEEK = Object.values(DaysOfWeek);
export const OFFER_ACCESS_TYPES = Object.values(OfferAccessType);
export const Shipping_Region = Object.values(ShippingRegion);

const VALID_REQUIREMENTS = [
  'postOnInstagramStories',
  'postOnInstagramStories3x',
  'postOnInstagramStories2x',
  'postOnInstagramFeed',
  'postOnInstagramReels',
  'postOnTikTokVideo',
] as const;

const REQUIREMENTS = ['noRequirement', ...VALID_REQUIREMENTS] as const;

export type OfferType = (typeof OFFER_TYPES)[number];
export type Requirement = (typeof REQUIREMENTS)[number];

export const waitTimeOptions = [
  { label: 'Everyday', value: 0 },
  { label: '3 times per week', value: 2 },
  { label: '1 time per week', value: 6 },
  { label: '1 time every two weeks', value: 13 },
  { label: '1 time per month', value: 30 },
  { label: '1 time every two months', value: 60 },
  { label: '1 time per year', value: 364 },
  { label: '1 time only', value: 10000 },
];

export const waitTimeOptionsV2 = (repeatDaysLength: number) => [
  { label: 'Unlimited', value: 0, premium: false, disabled: false },
  { label: '3 times per week', value: 2, premium: false, disabled: repeatDaysLength < 3 },
  { label: '1 time per week', value: 6, premium: false, disabled: false },
  { label: '1 time per month', value: 30, premium: false, disabled: false },
  { label: '1 time only', value: 10000, premium: true, disabled: false },
];

export const schema = yup.object({
  type: yup.mixed<OfferType>().oneOf(OFFER_TYPES).required(),
  offerImageKey: yup.string().required(),
  category: yup.mixed<OfferCategory>().oneOf(OFFER_CATEGORIES).required(),
  title: yup.string().trim().required(),
  description: yup.string().trim().max(800).required(),
  offerValue: yup.number().required().min(1, 'Offer value must be greater than or equal to 1'),
  minFollowers: yup.number().required().min(0).nullable(),
  requirements: yup.mixed<Requirement>().oneOf(REQUIREMENTS).required(),
  shippingRegions: yup.array(yup.mixed<ShippingRegion>().oneOf(Shipping_Region).required()),
  shopifyProductID: yup.string().nullable(),
  incentiveValue: yup
    .number()
    .nullable()
    .transform((value) => (!value ? undefined : value))
    .min(1, 'Incentive value must be greater than or equal to 1')
    .optional(),
  tag: yup.string().nullable(),
});

export type FormValue = yup.InferType<typeof schema>;

export const initialValues: FormValue = {
  type: 'public',
  offerImageKey: '',
  category: OfferCategory.product,
  title: '',
  description: '',
  offerValue: 0,
  minFollowers: 0,
  requirements: 'postOnTikTokVideo',
  shippingRegions: [ShippingRegion.global],
  shopifyProductID: null,
  incentiveValue: undefined,
  tag: '',
};

export const dateToDateString = (date: Date, time: Date) => {
  const dDate = dayjs(date);
  const dTime = dayjs(time);

  const dDateTime = dDate.hour(dTime.hour()).minute(dTime.minute());

  return dDateTime.format('YYYY-MM-DD HH:mm');
};

export const mapToOfferForm = (data: Offer): FormValue => {
  const req = VALID_REQUIREMENTS.find((x) => !!data[x]) ?? 'noRequirement';

  const startDateV2 =
    data.status === OfferStatus.active
      ? dayjs.utc(data?.startDateTime).format('YYYY-MM-DD')
      : data?.startDate;
  const startTimeV2 =
    data.status === OfferStatus.active
      ? dayjs.utc(data?.startDateTime).format('HH:mm')
      : data?.startTime;

  const startDateTime = datetimeWithTimezone({
    date: startDateV2,
    time: startTimeV2,
    timezoneId: data.timeZoneId,
  }).tz();
  const endDateTime = datetimeWithTimezone({
    date: data.endDate ?? data.startDate,
    time: data.endTime,
    timezoneId: data.timeZoneId,
  }).tz();

  const _start = getTimeInTimezone(startDateV2, startTimeV2, data?.timeZoneId);
  const _end = getTimeInTimezone(startDateV2, data?.endTime, data?.timeZoneId);

  return {
    requirements: req,
    offerImageKey: data.picture?.medium,
    type: data.private ? 'private' : 'public',
    startDate: startDateTime?.toDate(),
    startTime: dayjs(`${data.startDate} ${_start}`).toDate() ?? null,
    endTime: dayjs(`${data.startDate} ${_end}`).toDate() ?? null,
    ...(data.category !== 'event' && { endDate: data.endDate ? endDateTime.toDate() : null }),
    ...pick(data, [
      'category',
      'title',
      'description',
      'offerValue',
      'minFollowers',
      'shippingRegions',
      'shopifyProductID',
      'incentiveValue',
      'tag',
    ]),
  } as FormValue;
};

const callToConfirmAllowCategory: OfferCategory[] = [
  OfferCategory.beauty,
  OfferCategory.fitness,
  OfferCategory.experience,
  OfferCategory.boutique,
];

export const isAllowCallToConfirm = (isOnReg: boolean, category: OfferCategory): boolean =>
  isOnReg && callToConfirmAllowCategory.includes(category);

const mapToReqInput = (value: FormValue): Partial<VenueCreateOfferInput> => ({
  postOnInstagramFeed: null,
  postOnInstagramStories: null,
  postOnInstagramReels: null,
  postOnInstagramStories3x: null,
  postOnInstagramStories2x: null,
  postOnTikTokVideo: null,
  ...(value.requirements !== 'noRequirement' ? { [value.requirements]: true } : {}),
});

const mapToCommonOfferInput = (
  values: FormValue,
  timezoneId: string | null,
): Omit<VenueCreateOfferInput, 'venueID'> => {
  const isPublic = values.type === 'public';

  const offerValue = values.offerValue ?? 0;
  const req = mapToReqInput(values);

  const dateAndTime = dayjs.utc().tz(timezoneId || '');
  const endDate = dayjs.utc().add(5, 'year');

  return {
    startDate: dateAndTime.format('YYYY-MM-DD') ?? '',
    startTime: dateAndTime.format('HH:mm') ?? '',
    endTime: endDate.format('HH:mm') ?? '',
    endDate: endDate.format('YYYY-MM-DD') ?? '',
    status: OfferStatus.pending,
    availableTo: [Profession.model],
    gender: [Gender.female],
    spots: 100000,
    waitTime: null,
    offerValue: offerValue,
    minFollowers: isPublic ? values.minFollowers : null,
    offerImageKey: values.offerImageKey.startsWith('https://') ? undefined : values.offerImageKey,
    suggestedTip: null,
    confirmWithCall: null,
    ...req,
    ...pick(values, [
      'category',
      'title',
      'description',
      'shippingRegions',
      'shopifyProductID',
      'incentiveValue',
      'tag',
    ]),
  };
};

export const mapToCreateOfferInput = (
  venueID: string,
  values: FormValue,
  timezoneId?: string | null,
): VenueCreateOfferInput => {
  const isPublic = values.type === 'public';
  const commonInput = mapToCommonOfferInput(values, timezoneId ?? null);

  return {
    venueID,
    private: !isPublic,
    ...commonInput,
    ...(!isPublic && { startTime: '00:00', endTime: '00:00' }),
  };
};

export const mapToUpdateOfferInput = (
  id: string,
  values: FormValue,
  timezoneId?: string | null,
): VenueUpdateOfferInput => {
  const isPublic = values.type === 'public';
  const commonInput = mapToCommonOfferInput(values, timezoneId ?? null);
  return {
    id,
    offerImageKey: values.offerImageKey.startsWith('https://') ? undefined : values.offerImageKey,
    ...commonInput,
    ...(!isPublic && { startTime: '00:00', endTime: '00:00' }),
  };
};

export const stepOneSchema = yup.object({
  type: yup.mixed<OfferType>().oneOf(OFFER_TYPES).required(),
  offerImageKey: yup.string().required(),
  category: yup.mixed<OfferCategory>().oneOf(OFFER_CATEGORIES).required(),
  title: yup.string().trim('Product title cannot contain leading or trailing spaces').required('Product title is required'),
  description: yup.string().trim('Description cannot contain leading or trailing spaces').max(500, 'You have exceeded the 500-character limit').required('Description is required'),
  offerValue: yup.number().required().min(1, 'Offer value must be greater than or equal to 1'),
});

export const stepTwoSchema = yup.object({
  minFollowers: yup
    .number()
    .when('type', {
      is: 'public',
      then: (schema) => schema.required().min(0),
    })
    .nullable(),
  requirements: yup.mixed<Requirement>().oneOf(REQUIREMENTS).required(),
  incentiveValue: yup
    .number()
    .nullable()
    .transform((value) => (!value ? undefined : value))
    .min(1, 'Incentive value must be greater than or equal to 1')
    .optional(),
  tag: yup.string().nullable(),
});

export const stepThreeSchema = yup.object({
  shippingRegions: yup
    .array()
    .of(yup.mixed<ShippingRegion>().oneOf(Object.values(Shipping_Region)).required('At least one shipping region is required'))
    .min(1, 'At least one shipping region is required'),
});

export type StepOneFormValue = yup.InferType<typeof stepOneSchema>;
export type StepTwoFormValue = yup.InferType<typeof stepTwoSchema>;
export type StepThreeFormValue = yup.InferType<typeof stepThreeSchema>;
